import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle2, Loader2, RefreshCw } from 'lucide-react';

export default function CompleteComplianceDialog({ open, onOpenChange, item, onSuccess }) {
  const { authAxios } = useAuth();
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const isRecurring = item?.recurrence?.interval && item?.recurrence?.interval !== 'one-time';

  const handleComplete = async () => {
    setSaving(true);
    try {
      const api = authAxios();
      const res = await api.post(`/compliance-items/${item.compliance_id}/complete`, {
        notes: notes,
      });

      if (res.data.next_occurrence) {
        toast.success('Item afgerond — volgend item aangemaakt', {
          description: `Volgende deadline: ${new Date(res.data.next_occurrence.due_date).toLocaleDateString('nl-NL')}`,
        });
      } else {
        toast.success('Compliance item afgerond');
      }
      setNotes('');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij afronden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="compliance-complete-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            Markeren als afgerond
          </DialogTitle>
          <DialogDescription>
            Voeg optioneel een afrondnotitie toe. Dit wordt gelogd voor audit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
            <p className="text-sm font-medium text-slate-900">{item?.title}</p>
            <p className="text-xs text-slate-500 mt-1">
              Vervaldatum: {item?.due_date ? new Date(item.due_date).toLocaleDateString('nl-NL') : '—'}
            </p>
          </div>

          {isRecurring && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-50 border border-blue-200">
              <RefreshCw className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                Dit is een herhalend item. Na afronden wordt automatisch een volgend item aangemaakt.
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="complete-notes" className="text-xs font-medium">Afrondnotitie</Label>
            <Textarea
              id="complete-notes"
              data-testid="compliance-complete-notes-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Bijv. Keuring goedgekeurd, geen bijzonderheden."
              rows={3}
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Annuleren
          </Button>
          <Button
            onClick={handleComplete}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="compliance-complete-confirm-button"
          >
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Afronden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
